import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useFormContext, useFieldArray } from "react-hook-form";
import {
  MotifButton,
  MotifFormField,
  MotifLabel,
  MotifIcon,
  MotifSelect,
  MotifOption,
  MotifDropdown,
  MotifDropdownItem,
} from "@ey-xd/motif-react";
import {
  MotifActionIcDelete24px,
  IconoirNavArrowDown,
} from "@ey-xd/motif-icon";
import ControlSelectBoxAttribute from "./ControlSelectBoxAttribute";
import ControlSelectBoxOperator from "./ControlSelectBoxOperator";
import ValueElement from "./ValueElement";
import { useGetOperatorListQuery } from "@/services/advanceSearch/advanceSearchReducer";
import Group from "./Group";
import "./Tree.css";
import CommonTooltipWrapper from "../Tooltip/CommonTooltipWrapper";

type TreeProps = {
  attributes: {
    propertyName: string;
    entityName: string;
    attributeType: number;
    entityCategory: number;
    displayName?: string;
    parentEntityName?: string;
  }[];
  pageDisable?: boolean;
};
const Tree = ({ attributes, pageDisable }: TreeProps) => {
  const { t } = useTranslation();
  const { data: attributeOperators } = useGetOperatorListQuery();
  const [showMenu, setShowMenu] = useState(false);
  const { control, setValue, watch, clearErrors } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "treeRows",
  });
  const logicalOperator = watch("logicalOperator");
  const treeRows = watch("treeRows");
  const handleAttributeChange = (
    index: number,
    attribute: string,
    groupIndex?: number
  ) => {
    const selectedAttr = attributes.find(
      (attr) => `${attr.entityName}.${attr.propertyName}` === attribute
    );
    if (selectedAttr) {
      if (groupIndex !== undefined) {
        updateTreeValue(
          `treeRows.${index}.treeRows.${groupIndex}`,
          selectedAttr
        );
      }
      if (index !== undefined) {
        updateTreeValue(`treeRows.${index}`, selectedAttr);
      }

      const basePath =
        groupIndex !== undefined
          ? `treeRows.${index}.treeRows.${groupIndex}`
          : `treeRows.${index}`;
      setValue(`${basePath}.operator`, "");
      setValue(`${basePath}.value`, "");
    }
  };

  const handleOperatorChange = (
    index: number,
    operator: string,
    groupIndex?: number
  ) => {
    const basePath =
      groupIndex !== undefined
        ? `treeRows.${index}.treeRows.${groupIndex}`
        : `treeRows.${index}`;
    setValue(`${basePath}.operator`, operator);
    setValue(`${basePath}.value`, "");
  };
  let  updateTreeValue=useCallback((basePath: string, attributeData: any)=>{
    setValue(`${basePath}.attributeType`, attributeData.attributeType);
    setValue(`${basePath}.entityName`, attributeData.entityName);
    setValue(`${basePath}.entityCategory`, attributeData.entityCategory);
    setValue(`${basePath}.parentEntityName`, attributeData.parentEntityName);
    setValue(`${basePath}.propertyName`, attributeData.propertyName);
    let endpoint = attributeData.apiEndpoint;
    if(!endpoint && [4,5].includes(attributeData.attributeType)){
      endpoint = "Search/ListCustomAttributeValues"
    }

    setValue(`${basePath}.apiEndpoint`, endpoint);
  },[setValue])
  useEffect(() => {
    if (treeRows.length > 0) {
      treeRows.forEach((item: any, index: number) => {
        if (item.type === "row") {
          const initialAttribute: any = attributes.find(
            (attr) => attr.displayName === item.attribute  || `${attr.entityName}.${attr.propertyName}` === `${item.entityName}.${item.propertyName}`
          );

          if (initialAttribute) {
            updateTreeValue(`treeRows.${index}`, initialAttribute);
          }
        } else if (
          item.type === "group" &&
          item.treeRows &&
          item.treeRows.length > 0
        ) {
          item.treeRows.forEach((subItem: any, subIndex: number) => {
            const initialSubAttribute = attributes.find(
              (attr) => attr.displayName === subItem.attribute || `${attr.entityName}.${attr.propertyName}` === `${subItem.entityName}.${subItem.propertyName}`
            );
            if (initialSubAttribute) {
              updateTreeValue(
                `treeRows.${index}.treeRows.${subIndex}`,
                initialSubAttribute
              );
            }
          });
        }
      });
    }
  }, [attributes, treeRows,updateTreeValue]);

  useEffect(() => {
    treeRows.forEach((item: any, index: number) => {
      if (item.type === "row" && shouldHideValueElement(item.operator)) {
        setValue(`treeRows.${index}.value`, "");
        clearErrors(`treeRows.${index}.value`);
      }
      if (item.treeRows && item.treeRows.length > 0) {
        item.treeRows.forEach((subItem: any, subIndex: number) => {
          if (
            subItem.type === "row" &&
            shouldHideValueElement(subItem.operator)
          ) {
            setValue(`treeRows.${index}.treeRows.${subIndex}.value`, "");
            clearErrors(`treeRows.${index}.treeRows.${subIndex}.value`);
          }
        });
      }
    });
  }, [treeRows, setValue, clearErrors]);

  const shouldHideValueElement = (operator: string) => {
    return [
      "Contains data",
      "Does not contain data",
      "Last 7 days",
      "Last 30 days",
    ].includes(operator);
  };
  const addGroup = () => {
    append({
      type: "group",
      logicalOperator: "0",
      treeRows: [{ attribute: "", operator: "", value: "", type: "row" }],
    });
  };

  if (!attributeOperators || Object.keys(attributeOperators).length === 0) {
    return null;
  }
  return (
    <div className="tree-container">
      {fields && fields.length > 0 && (
        <div className="motif-row">
          <div className="motif-col-md-2 motif-col-lg-2">
            <MotifSelect
              ariaLabelledBy="select-label"
              onChange={(value) => {
                setValue("logicalOperator", value);
              }}
              value={logicalOperator.toString()}
              visibleOptions={3}
              className="select-box-width"
              disabled={pageDisable}
            >
              <MotifOption value="0">
                {t("pages.configuration.scheme.rule.and")}
              </MotifOption>
              <MotifOption value="1">
                {t("pages.configuration.scheme.rule.or")}
              </MotifOption>
            </MotifSelect>
          </div>
        </div>
      )}
      {fields.map((field: any, index) => (
        <div key={field.id}>
          {field.type === "row" ? (
            <div className="motif-row">
              <div className="motif-col-md-1 motif-col-lg-1 horizontal-line-container">
                <div className="vertical-line"></div>
                <div className="horizontal-line mar-b-20"></div>
              </div>
              <div className="motif-col-md-2 mar-b-20">
                {attributes && attributes.length > 0 && (
                  <ControlSelectBoxAttribute
                    disable={pageDisable}
                    index={index}
                    control={control}
                    attributes={attributes}
                    handleAttributeChange={handleAttributeChange}
                  />
                )}
              </div>
              <div className="motif-col-md-2 mar-b-20">
                <ControlSelectBoxOperator
                  disable={pageDisable}
                  index={index}
                  control={control}
                  attributeOperators={attributeOperators}
                  treeRows={treeRows}
                  handleOperatorChange={handleOperatorChange}
                />
              </div>
              <div style={{marginTop:"2px"}} className="motif-col-md-2 mar-b-20 ">
                {!shouldHideValueElement(treeRows[index]?.operator) && (
                  <MotifLabel aria-label="Input" htmlFor="input">
                    {t("pages.configuration.scheme.rule.value")}
                  </MotifLabel>
                )}

                <MotifFormField aria-label="Input" role="group">
                  {!shouldHideValueElement(treeRows[index]?.operator) && (
                    <ValueElement
                      // dropDownOptions={attributeOperatorsData}
                      disable={pageDisable}
                      index={index}
                      control={control}
                      treeRows={treeRows}
                    />
                  )}
                </MotifFormField>
              </div>
              {
                <div className="motif-col-md-1 mar-b-20">
                  {!pageDisable && (
                    <CommonTooltipWrapper
                      tooltipTitle={t("global.globalActions.delete")}
                    >
                      <MotifButton
                        onClick={() => {
                          remove(index);
                        }}
                        size="small"
                        type="button"
                        variant="secondary"
                        className="delete-button"
                      >
                        <MotifIcon iconFunction={MotifActionIcDelete24px} />
                      </MotifButton>
                    </CommonTooltipWrapper>
                  )}
                </div>
              }
            </div>
          ) : (
            <Group
              pageDisable={pageDisable}
              index={index}
              setValue={setValue}
              field={field}
              control={control}
              remove={remove}
              attributes={attributes}
              handleAttributeChange={handleAttributeChange}
              handleOperatorChange={handleOperatorChange}
              treeRows={treeRows}
              shouldHideValueElement={shouldHideValueElement}
              attributeOperators={attributeOperators}
            />
          )}
        </div>
      ))}
      <div className="motif-row">
        {!pageDisable && (
          <div className="motif-col-md-2 motif-col-lg-2">
            <MotifDropdown
              open={showMenu}
              ariaLabelledby={"AddDropdown"}
              hideArrow={true}
              handleClickOutside={() => setShowMenu(false)}
              trigger={
                <MotifButton
                  type="button"
                  onClick={() => setShowMenu(!showMenu)}
                  id={"addId"}
                  aria-label="Add"
                  variant="secondary"
                >
                  {t("pages.configuration.scheme.rule.add")}
                  <MotifIcon iconFunction={IconoirNavArrowDown} />
                </MotifButton>
              }
            >
              <MotifDropdownItem
                className="add-dropdown-width"
                onClick={() =>
                  append({
                    type: "row",
                    attribute: "",
                    operator: "",
                    value: "",
                  })
                }
              >
                {t("pages.configuration.scheme.rule.addRow")}
              </MotifDropdownItem>
              <MotifDropdownItem
                className="add-dropdown-width"
                onClick={addGroup}
              >
                {t("pages.configuration.scheme.rule.addGroup")}
              </MotifDropdownItem>
            </MotifDropdown>
          </div>
        )}
      </div>
    </div>
  );
};

export default Tree;
